import { Datepicker } from "@mobiscroll/react";
import React, { useEffect, useState } from "react";
import { NavDropdown } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { GridUsersDropdown } from "../../../grid/GridUsersDropdown";
import { UsersDto } from "users-experience-client";
import { Lookup, WorkflowState } from "common-util-experience-client";
import { GridWorkflowStatusDropdown } from "../../../grid/GridWorkflowStatusDropdown";
import { GridLookupDropdown } from "../../../grid/GridLookupDropdown";
import { WorkflowConstants } from "../../../helper/WorkflowConstants";
import Lookups, { Constants } from "../../../constants/Lookups";
import { GridDropdown } from "../../../grid/GridDropdown";
import { AppointmentCategory, AppointmentMode, AppointmentType, FilterCriteria } from "appointment-experience-client";
import { createAppointmentApi } from "../../../api-helpers/AppointmentApiHelper";

interface ScheduleHeaderProps {
  patientId?: string;
  currentUserId: string;
  offCanvasShowCallback: (comp: string) => void;
  onUpdateFilters: (filterArray: FilterCriteria[]) => void;
  breadcrumbs: boolean | undefined;
  scope: 'Patient' | 'Global';
}

const getTodayRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  return [start, end];
};

const ScheduleHeader = (props: ScheduleHeaderProps) => {
  const appointmentApi = createAppointmentApi();
  const [searchText, setSearchText] = useState<string>();
  const [appointmentModes, setAppointmentModes] = useState<
    AppointmentMode[] | null
  >();
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentCategory[]>([]);

  const [dateRange, setDateRange] = useState<Date[] | []>(() => (props.patientId ? [] : getTodayRange()));
  const [assignedTo, setAssignedTo] = useState<string[] | null>();
  const [apptType, setApptType] = useState<AppointmentType[]>();
  const [statuses, setStatuses] = useState<WorkflowState[] | null>();
  const [appointmentCategories, setAppointmentCategories] = useState<
    string[] | null
  >();
  useEffect(() => {
    props.scope === 'Patient' ? setAssignedTo([]) : setAssignedTo([props.currentUserId]);
}, [props.scope]);

  const updateFilters = () => {
    const filters: FilterCriteria[] = [];
    if (props.patientId) {
      filters.push({
        propertyName: "patientId",
        operator: "equals",
        propertyValue: props.patientId,
      });
    }
    if ((searchText?.length ?? 0) > 0) {
      filters.push({
        propertyName: "appointmentType",
        operator: "contains",
        propertyValue: searchText,
      });
    }
    if (dateRange.length){
      filters.push({
        propertyName: "time",
        operator: "greater-than-equals",
        propertyValue: dateRange[0],
      });
      filters.push({
        propertyName: "time",
        operator: "less-than-equals",
        propertyValue: dateRange[1],
      });
    }
    if (assignedTo && assignedTo.length > 0) {
      filters.push({
        propertyName: "coachUserId",
        operator: "in",
        propertyValue: assignedTo,
      });
    }
    if (appointmentTypes && appointmentTypes.length > 0) {
      filters.push({
        propertyName: "appointmentCategoryId",
        operator: "in",
        propertyValue: appointmentTypes.map((t) => t.id),
      });
    }
    if (appointmentModes && appointmentModes.length > 0) {
      filters.push({
        propertyName: "appointmentModeId",
        operator: "in",
        propertyValue: appointmentModes.map((p) => p.id),
      });
    }
    if (statuses && statuses.length > 0) {
      filters.push({
        propertyName: "wfStateId",
        operator: "in",
        propertyValue: statuses.map((p) => p.stateId),
      });
    }
    if(apptType && apptType.length > 0){
      filters.push({
        propertyName: "appointmentTypeId",
        operator: "in",
        propertyValue: apptType.map(t=>t.id),
      });
    }
    props.onUpdateFilters(filters);
  };

  useEffect(updateFilters, [
    searchText,
    dateRange,
    assignedTo,
    statuses,
    appointmentModes,
    appointmentCategories,
    appointmentTypes,
    apptType
  ]);

  const handleOffCanvasShow = (comp: string) => {
    props.offCanvasShowCallback(comp);
  };

  const handleDateChange = (event: any) => {
    const [start, end] = event.value;
  
    // Set start date to local 00:00:00
    const startDate = new Date(start);
    startDate.setHours(0, 0, 0, 0);
  
    // Set end date to local 23:59:59
    let endDate;
    if (!end) {
        endDate = new Date(startDate);
    } else {
        endDate = new Date(end);
    }
    endDate.setHours(23, 59, 59, 999);
  
    setDateRange([startDate, endDate]);
  };
  const loadAppointmentTypes = async (): Promise<AppointmentCategory[]> => {
        const response = await appointmentApi.appointmentControllerGetAllCategories();
        return response.data;
    };

  const loadDataApptTypes = async (searchText?: string): Promise<AppointmentType[]> => {
    const category =await appointmentApi.appointmentControllerGetAppointmentCategory(Constants.APP_ONE_ONE);
    const response =await appointmentApi.appointmentControllerGetAppointmentTypesByCategory(category.data.id);
    if (Array.isArray(response.data)) {
        return response.data
    }else{
        return[]
    }
  };

  const updateAssignedTo = (values: UsersDto[]) => {
    setAssignedTo(values ? values.map((v) => v.id) : null);
  };

  const updateAppointmentCategory = (values: Lookup[]) => {
    setAppointmentCategories(values.map((v) => v.id));
  };

  const loadAppointmentModes = async (): Promise<AppointmentMode[]> => {
    const response =
      await appointmentApi.appointmentControllerGetAppointmentMode();
    return response.data;
  };

  return (
    <>
    {props.breadcrumbs && 
      <div
        className="d-flex align-items-center px-3 px-md-4 px-lg-5 py-2 border-bottom"
        aria-label="breadcrumb"
      >
        <div className="flex-fill d-flex align-items-center">
            <div>
              <ol className="breadcrumb">
                <li className="breadcrumb-item breadcrumb-item-first">
                  <Link to="/home">
                  <svg fill="none" viewBox="0 0 21 19" className='svg-0-875 align-text-top'>
                      <path d="M10.5 3.34824L15.5 8.11294V16.3824H13.5V10.0294H7.5V16.3824H5.5V8.11294L10.5 3.34824ZM10.5 0.5L0.5 10.0294H3.5V18.5H9.5V12.1471H11.5V18.5H17.5V10.0294H20.5L10.5 0.5Z" />
                  </svg>
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Schedule
                </li>
              </ol>
              <h1 className="h3 m-0">Schedule</h1>
            </div>
        </div>
        <div className="justify-content-end d-flex align-items-center">
          <button
            className="btn btn-sm btn-secondary ms-md-3 px-3 mt-2 mt-md-0 rounded-pill pe-4"
            onClick={() => handleOffCanvasShow("newAppointmentV2")}
          >
            <span className="material-symbols-outlined fs-16 align-text-top">add</span>
            {" "}New Appointment
          </button>
        </div>
      </div>}

      <div className="d-md-flex align-items-center px-3 px-md-4 px-lg-5 py-2 border-bottom">
        <div className="flex-fill d-flex align-items-center">
          <div className="search-box max-w-md-18-75">
            <span className="material-symbols-outlined search-box__icon">
              search
            </span>
            <div className="typeahead-container search-box d-flex">
              <span className="material-symbols-outlined search-box__icon">
                search
              </span>
              <input
                type="text"
                name="searchAppointment"
                className="form-control search-box__input fw-normal fs-12"
                placeholder="Search Appointment"
                onChange={(event) => setSearchText(event.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="d-flex flex-wrap align-items-center">
        <GridDropdown
          label={"Select Appt. Type"}
          keyProvider={appointmentType => appointmentType.id}
          labelProvider={appointmentType => appointmentType.name!}
          dataProvider={loadDataApptTypes}
          onChange={setApptType}
          ></GridDropdown>
          <NavDropdown
            id="nav-dropdown-dark-example"
            title="Due Date"
            className={`btn-dropdown ms-md-2 me-2 me-md-0 mt-2 mt-md-0 ${dateRange.length ? 'border border-primary rounded-2' : 'border border-white'}`}
          >
            <div style={{ width: "500px" }}>
              <Datepicker
                controls={["calendar"]}
                select="range"
                theme="windows"
                themeVariant="light"
                calendarType="month"
                pages={2}
                touchUi={true}
                display="inline"
                value={dateRange}
                onChange={handleDateChange}
              />
            </div>
          </NavDropdown>
          
          <GridWorkflowStatusDropdown
            label="Status"
            type={WorkflowConstants.appointmentWorkflowType}
            onChange={setStatuses}
          ></GridWorkflowStatusDropdown>
          <GridUsersDropdown
            label="Coach"
            initialUserIds={assignedTo ?? []}
            onChange={updateAssignedTo}
          ></GridUsersDropdown>
          <GridDropdown
              label="Category"
              keyProvider={item => item.id}
              labelProvider={item => item.name!}
              dataProvider={loadAppointmentTypes}
              onChange={setAppointmentTypes}
          ></GridDropdown>

          {!props.breadcrumbs && (
            <div className="justify-content-end d-flex align-items-center">
              <button
                className="btn btn-sm btn-secondary ms-md-3 px-3 mt-2 mt-md-0 rounded-pill pe-4"
                onClick={() => handleOffCanvasShow("newAppointmentV2")}
              >
                <span className="material-symbols-outlined fs-16 align-text-top">add</span>
                {" "}New Appointment
              </button>
            </div>
          )}


        
          {/* <GridDropdown
            label="Mode"
            keyProvider={(item) => item.appointmentModeId}
            labelProvider={(item) => item.appointmentMode!}
            dataProvider={loadAppointmentModes}
            onChange={setAppointmentModes}
          ></GridDropdown> */}
        </div>
      </div>
    </>
  );
};

export default ScheduleHeader;
